/**
 * Server info: edition detection and version.
 *
 * Called once on app boot via the useServerInfo hook. The result decides
 * which auth flow the embedded SPA presents:
 *
 *   - edition === "oss":  the binary is the public hivehook image. The
 *                         operator authenticates with an API key via
 *                         <AuthGate>. No signup/login UI is shown.
 *   - edition === "saas": the binary is hivehook-saas (private hosted
 *                         service). Users authenticate via session cookie
 *                         and are redirected to /signup or /login if
 *                         no valid session exists.
 *
 * The endpoint is unauthenticated by design so a fresh visitor can land
 * on /signup without any cookie and we still know which UI to render.
 */

export type Edition = "oss" | "saas"

export interface ServerInfo {
  version: string
  edition: Edition
  /**
   * True when the SaaS operator has flipped maintenance mode. Auth pages
   * (/signup, /login) render a "launching soon" card instead of the form
   * so users don't fill out something that's already being rejected at
   * the API level. Always false on OSS.
   */
  maintenance: boolean
}

let cachedPromise: Promise<ServerInfo> | null = null

export function fetchServerInfo(): Promise<ServerInfo> {
  if (cachedPromise) return cachedPromise

  const p: Promise<ServerInfo> = fetch("/api/v1/server/info", {
    method: "GET",
    headers: { Accept: "application/json" },
    credentials: "include",
  })
    .then(async (res): Promise<ServerInfo> => {
      if (!res.ok) {
        // Fall back to OSS so a broken endpoint doesn't lock self-hosters out.
        return { version: "unknown", edition: "oss", maintenance: false }
      }
      const body = (await res.json()) as Partial<ServerInfo>
      const edition: Edition = body.edition === "saas" ? "saas" : "oss"
      return {
        version: body.version ?? "unknown",
        edition,
        maintenance: body.maintenance === true,
      }
    })
    .catch((): ServerInfo => {
      return { version: "unknown", edition: "oss", maintenance: false }
    })

  cachedPromise = p
  return p
}

/** Test helper to clear the cached promise. */
export function _resetServerInfoCacheForTests() {
  cachedPromise = null
}
